import React, { useEffect, useRef } from 'react';

class Being {
  constructor(x, y, canvas) {
    this.x = x;
    this.y = y;
    this.canvas = canvas;
    this.speed = 0.8;
    this.angle = Math.random() * Math.PI * 2;
    this.radius = 2;
    this.baseHue = Math.random() * 60 + 280;
    this.connectionRadius = 150;
    this.connectionStrength = 0;
    this.lastConnectionTime = 0;
    this.targetX = Math.random() * canvas.width;
    this.targetY = Math.random() * canvas.height;
    this.targetChangeInterval = 2000 + Math.random() * 3000;
    this.lastTargetChange = Date.now();
  }

  update(time) {
    if (time - this.lastTargetChange > this.targetChangeInterval) {
      this.targetX = Math.random() * this.canvas.width;
      this.targetY = Math.random() * this.canvas.height;
      this.lastTargetChange = time;
      this.targetChangeInterval = 2000 + Math.random() * 3000;
    }

    const dx = this.targetX - this.x;
    const dy = this.targetY - this.y;
    const dist = Math.sqrt(dx * dx + dy * dy);
    
    if (dist > this.speed) {
      this.x += (dx / dist) * this.speed;
      this.y += (dy / dist) * this.speed;
    }

    // Keep beings within viewport bounds
    if (this.x < 0) this.x = 0;
    if (this.x > this.canvas.width) this.x = this.canvas.width;
    if (this.y < 0) this.y = 0;
    if (this.y > this.canvas.height) this.y = this.canvas.height;

    this.connectionStrength = Math.max(0, this.connectionStrength - 0.01);
  }

  connect(other, ctx, scale) {
    const dx = other.x - this.x;
    const dy = other.y - this.y;
    const dist = Math.sqrt(dx * dx + dy * dy);

    if (dist < this.connectionRadius) {
      const newStrength = (1 - dist / this.connectionRadius) * 0.8;
      this.connectionStrength = Math.max(this.connectionStrength, newStrength);
      other.connectionStrength = Math.max(other.connectionStrength, newStrength);
      
      const gradient = ctx.createLinearGradient(
        this.x * scale, 
        this.y * scale, 
        other.x * scale, 
        other.y * scale
      );
      gradient.addColorStop(0, `hsla(${this.baseHue}, 100%, 60%, ${this.connectionStrength})`);
      gradient.addColorStop(1, `hsla(${other.baseHue}, 100%, 60%, ${other.connectionStrength})`);
      
      ctx.beginPath();
      ctx.strokeStyle = gradient;
      ctx.lineWidth = 2 * this.connectionStrength;
      ctx.moveTo(this.x * scale, this.y * scale);
      ctx.lineTo(other.x * scale, other.y * scale);
      ctx.stroke();
    }
  }

  draw(ctx, scale) {
    ctx.save();
    ctx.fillStyle = `hsla(${this.baseHue}, 100%, 60%, ${0.5 + this.connectionStrength * 0.5})`;
    ctx.shadowColor = `hsla(${this.baseHue}, 100%, 60%, 0.8)`;
    ctx.shadowBlur = 15;
    ctx.beginPath();
    ctx.arc(
      this.x * scale, 
      this.y * scale, 
      this.radius * (1 + this.connectionStrength), 
      0, 
      Math.PI * 2
    );
    ctx.fill();
    ctx.restore();
  }
}

const CanvasExperience = () => {
  const canvasRef = useRef(null);
  const beingsRef = useRef([]);
  const rafRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    let beings = [];
    const gridSize = 100;
    let grid = {};

    const resize = () => {
      // Get actual viewport dimensions
      const width = window.innerWidth;
      const height = window.innerHeight;
      const dpr = window.devicePixelRatio || 1;
      
      // Set physical canvas size
      canvas.width = width * dpr;
      canvas.height = height * dpr;
      
      // Set display size
      canvas.style.width = `${width}px`;
      canvas.style.height = `${height}px`;
      
      // Initialize beings using viewport dimensions
      beings = Array.from({ length: 60 }, () => 
        new Being(
          Math.random() * width,
          Math.random() * height,
          { width, height }
        )
      );
      beingsRef.current = beings;
    };

    const updateGrid = () => {
      grid = {};
      for (const being of beings) {
        const cellX = Math.floor(being.x / gridSize);
        const cellY = Math.floor(being.y / gridSize);
        const key = `${cellX},${cellY}`;
        if (!grid[key]) grid[key] = [];
        grid[key].push(being);
      }
    };

    const animate = () => {
      const dpr = window.devicePixelRatio || 1;
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      const time = Date.now();
      
      beings.forEach(being => being.update(time));
      updateGrid();
      
      for (const key in grid) {
        const cellBeings = grid[key];
        for (let i = 0; i < cellBeings.length; i++) {
          for (let j = i + 1; j < cellBeings.length; j++) {
            cellBeings[i].connect(cellBeings[j], ctx, dpr);
          }
        }
      }

      beings.forEach(being => being.draw(ctx, dpr));
      rafRef.current = requestAnimationFrame(animate);
    };

    window.addEventListener('resize', resize);
    resize();
    animate();

    return () => {
      window.removeEventListener('resize', resize);
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
      }
    };
  }, []);

  return (
    <canvas 
      ref={canvasRef} 
      className="fixed inset-0 w-full h-full pointer-events-none" 
      style={{ zIndex: 1 }} 
    />
  );
};

export default CanvasExperience;
